import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import imageCompression from 'browser-image-compression';
import { UserData, UserHistory } from '../../../types/user';
import { logger } from '../../../utils/logger';
import { useCloudflareAuth } from '../../../contexts/CloudflareAuthContext';
import { formatTextField, formatPhoneForDisplay, getPhoneDigits } from '../../../utils/formHelpers';
import api from '../../../services/api';
import SearchableSelect, { SelectChangeEvent } from '../../../components/SearchableSelect';

const EDUCATION_OPTIONS = ['8th', 'High School', 'Intermediate', 'Graduate', 'Post Graduate'];

// Same list the Telegram bot offers (labels match, saved comma-separated)
const LANGUAGE_OPTIONS: Array<[string, string]> = [
  ['English', 'English'], ['Hindi', 'Hindi'], ['Assamese', 'Assamese'], ['Bengali', 'Bengali'],
  ['Bodo', 'Bodo'], ['Dogri', 'Dogri'], ['Gujarati', 'Gujarati'], ['Kannada', 'Kannada'],
  ['Kashmiri', 'Kashmiri'], ['Konkani', 'Konkani'], ['Maithili', 'Maithili'], ['Malayalam', 'Malayalam'],
  ['Manipuri', 'Manipuri'], ['Marathi', 'Marathi'], ['Nepali', 'Nepali'], ['Odia', 'Odia'],
  ['Punjabi', 'Punjabi'], ['Sanskrit', 'Sanskrit'], ['Santali', 'Santali'], ['Sindhi', 'Sindhi'],
  ['Tamil', 'Tamil'], ['Telugu', 'Telugu'], ['Urdu', 'Urdu'],
];

const WITNESS_FIELDS = ['witness1Name', 'witness1Phone', 'witness1Aadhaar', 'witness2Name', 'witness2Phone', 'witness2Aadhaar'];

const UserDetails: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, userDetails: authUserDetails, loading: authLoading, refreshUserDetails } = useCloudflareAuth();
  const [isLoading, setIsLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isFetching, setIsFetching] = useState(true);
  const [languagesOpen, setLanguagesOpen] = useState(false);
  const [languagesSearch, setLanguagesSearch] = useState('');
  const [customLanguages, setCustomLanguages] = useState<string[]>([]);
  const [customLanguageMode, setCustomLanguageMode] = useState(false);
  const [customLanguage, setCustomLanguage] = useState('');
  const [fieldStatus, setFieldStatus] = useState<Record<string, 'idle' | 'saving' | 'saved' | 'error'>>({});
  const saveTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const latestValuesRef = useRef<Record<string, any>>({});
  const inFlightRef = useRef<Set<string>>(new Set());
  const pendingValuesRef = useRef<Record<string, any>>({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [userData, setUserData] = useState<UserData>({
    fullName: '',
    email: '',
    phone: '',
    aadhaarNumber: '',
    memberType: 'National',
    memberStatus: 'Active',
    designation: '',
    officeState: '',
    officeDistrict: '',
    officeCityVillage: '',
    enrollmentNumber: '',
    range: '',
    rank: '',
    validFrom: new Date().toISOString().split('T')[0],
    currentAddressLine1: '',
    currentAddressLine2: '',
    currentState: '',
    currentDistrict: '',
    currentCityVillage: '',
    currentPinCode: '',
    permanentAddressLine1: '',
    permanentAddressLine2: '',
    permanentState: '',
    permanentDistrict: '',
    permanentCityVillage: '',
    permanentPinCode: '',
    sameAsCurrent: false,
    witness1Name: '',
    witness1Phone: '',
    witness1Aadhaar: '',
    witness2Name: '',
    witness2Phone: '',
    witness2Aadhaar: '',
    history: [],
    photo: '',
    aadhaarFront: '',
    aadhaarBack: '',
    vehicleRegistration: '',
    vehicleRegistrationUrl: '',
    promissoryNote: '',
    gender: '',
    dateOfBirth: '',
    bloodGroup: '',
    fatherOrHusbandName: '',
    education: '',
    languagesKnown: '',
    identificationMark: '',
    profession: '',
    vehicleMake: '',
    vehicleModel: '',
    profileUpdated: false
  });
  const [uploadProgress, setUploadProgress] = useState<{
    [key: string]: { status: 'idle' | 'uploading' | 'completed' | 'error'; progress: number }
  }>({
    photo: { status: 'idle', progress: 0 },
    aadhaarFront: { status: 'idle', progress: 0 },
    aadhaarBack: { status: 'idle', progress: 0 },
    vehicleRegistration: { status: 'idle', progress: 0 },
    promissoryNote: { status: 'idle', progress: 0 }
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [compressing, setCompressing] = useState(false);

  const getInputValue = (key: keyof UserData): string => {
    const value = userData[key];
    if (value instanceof Date) {
      return value.toISOString().split('T')[0];
    }
    if (typeof value === 'boolean') {
      return value.toString();
    }
    return value?.toString() || '';
  };

  // Per-field instant auto-save: PUT only the changed field, serialized per field
  const saveFieldNow = async (field: string, value: any) => {
    if (!currentUser) return;
    if (inFlightRef.current.has(field)) {
      pendingValuesRef.current[field] = value;
      return;
    }
    inFlightRef.current.add(field);
    setFieldStatus(prev => ({ ...prev, [field]: 'saving' }));
    try {
      await api.updateUser(currentUser.uid, { [field]: value ?? '' });
      setFieldStatus(prev => ({ ...prev, [field]: 'saved' }));
    } catch (error) {
      logger.error(`Auto-save failed for ${field}:`, error, { showToast: false });
      setFieldStatus(prev => ({ ...prev, [field]: 'error' }));
    } finally {
      inFlightRef.current.delete(field);
      const next = pendingValuesRef.current[field];
      if (next !== undefined) {
        delete pendingValuesRef.current[field];
        saveFieldNow(field, next);
      }
    }
  };

  const scheduleFieldSave = (field: string, value: any, delay = 600) => {
    latestValuesRef.current[field] = value;
    if (saveTimersRef.current[field]) clearTimeout(saveTimersRef.current[field]);
    saveTimersRef.current[field] = setTimeout(() => {
      delete saveTimersRef.current[field];
      saveFieldNow(field, latestValuesRef.current[field] ?? value);
    }, delay);
  };

  // Flush any debounced-but-unsaved values when the page is left, so nothing is lost
  useEffect(() => {
    const timersRefLocal = saveTimersRef;
    return () => {
      const fields = Object.keys(timersRefLocal.current);
      fields.forEach(f => clearTimeout(timersRefLocal.current[f]));
      timersRefLocal.current = {};
    };
  }, []);

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (Object.keys(saveTimersRef.current).length || inFlightRef.current.size) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const validatePincode = (pincode: string) => {
    // Allow empty or numbers up to 6 digits
    return pincode === '' || /^[0-9]{0,6}$/.test(pincode);
  };

  // Witness name/mobile/Aadhaar must not duplicate the other witness or the member's own
  const witnessDuplicateError = (field: string): string | null => {
    const isName = field.endsWith('Name');
    const isAadhaar = field.endsWith('Aadhaar');
    const norm = (v: any) => (isName ? String(v || '').trim().toLowerCase() : String(v || '').replace(/\D/g, ''));
    const value = norm(userData[field as keyof UserData]);
    if (!value) return null;
    const otherWitness = field.startsWith('witness1') ? '2' : '1';
    const memberKey = isName ? 'fullName' : isAadhaar ? 'aadhaarNumber' : 'phone';
    const memberLabel = isName ? 'Member name' : isAadhaar ? "Member's Aadhaar" : "Member's mobile";
    const candidates: Array<[string, string]> = [
      [`witness${otherWitness}${field.slice(8)}`, `Witness ${otherWitness}`],
      [memberKey, memberLabel],
    ];
    for (const [key, label] of candidates) {
      const other = norm(userData[key as keyof UserData]);
      if (other && other === value) return `Same as ${label}`;
    }
    return null;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement> | SelectChangeEvent) => {
    const { name } = e.target;
    const value = formatTextField(name, e.target.value);

    if (name === 'currentPinCode' || name === 'permanentPinCode') {
      const newValue = value.replace(/[^0-9]/g, '').slice(0, 6);
      if (!validatePincode(newValue)) {
        return;
      }
      e.target.value = newValue;
      setUserData(prev => {
        const newData = { ...prev, [name]: newValue };
        if (prev.sameAsCurrent && name.startsWith('current')) {
          const permanentField = 'permanent' + name.slice(7);
          newData[permanentField] = newValue;
        }
        return newData;
      });
      scheduleFieldSave(name, newValue);
      if (userData.sameAsCurrent && name.startsWith('current')) {
        scheduleFieldSave('permanent' + name.slice(7), newValue);
      }
      return;
    }

    const isSelect = !(e.target instanceof HTMLInputElement);
    if (!isSelect) {
      e.target.value = value;
    }

    setUserData(prev => {
      const newData = { ...prev, [name]: value };
      if (prev.sameAsCurrent && name.startsWith('current')) {
        const permanentField = 'permanent' + name.slice(7);
        newData[permanentField] = value;
      }
      return newData;
    });
    // Selects save instantly; typed input saves shortly after typing stops
    if (isSelect) {
      saveFieldNow(name, value);
      if (userData.sameAsCurrent && name.startsWith('current')) {
        saveFieldNow('permanent' + name.slice(7), value);
      }
    } else {
      scheduleFieldSave(name, value);
      if (userData.sameAsCurrent && name.startsWith('current')) {
        scheduleFieldSave('permanent' + name.slice(7), value);
      }
    }
  };

  // +91 prefix is shown statically next to the input, so the field holds only the 10 digits
  const handlePhoneDigits = (field: 'phone' | 'witness1Phone' | 'witness2Phone') => (e: React.ChangeEvent<HTMLInputElement>) => {
    let digits = e.target.value.replace(/\D/g, '');
    if (digits.length > 10 && digits.startsWith('91')) digits = digits.slice(2);
    if (digits.length > 10 && digits.startsWith('0')) digits = digits.replace(/^0+/, '');
    digits = digits.slice(0, 10);
    if (e.target instanceof HTMLInputElement) e.target.value = digits;
    const stored = digits ? `+91${digits}` : '';
    setUserData(prev => ({ ...prev, [field]: stored }));
    scheduleFieldSave(field, stored);
  };

  const handleLanguageToggle = (language: string) => {
    setUserData(prev => {
      const current = String(prev.languagesKnown || '').split(',').map(s => s.trim()).filter(Boolean);
      const next = current.includes(language) ? current.filter(l => l !== language) : [...current, language];
      const joined = next.join(', ');
      saveFieldNow('languagesKnown', joined);
      return { ...prev, languagesKnown: joined };
    });
  };

  // Manually added languages stay selectable (checkable) while the page is open
  const allLanguageOptions: Array<[string, string]> = [
    ...LANGUAGE_OPTIONS,
    ...customLanguages
      .filter(lang => !LANGUAGE_OPTIONS.some(([value]) => value === lang))
      .map(lang => [lang, lang] as [string, string]),
  ];

  const addCustomLanguage = () => {
    const language = customLanguage.trim();
    if (!language) return;
    setCustomLanguages(prev => (prev.includes(language) ? prev : [...prev, language]));
    handleLanguageToggle(language);
    setCustomLanguage('');
    setCustomLanguageMode(false);
  };

  const handleSameAsCurrentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setUserData(prev => ({
      ...prev,
      sameAsCurrent: checked,
      permanentAddressLine1: checked ? prev.currentAddressLine1 : '',
      permanentAddressLine2: checked ? prev.currentAddressLine2 : '',
      permanentState: checked ? prev.currentState : '',
      permanentDistrict: checked ? prev.currentDistrict : '',
      permanentCityVillage: checked ? prev.currentCityVillage : '',
      permanentPinCode: checked ? prev.currentPinCode : ''
    }));
    // Auto-save the copied (or cleared) permanent address fields
    const permanentPairs: Array<[string, any]> = checked ? [
      ['permanentAddressLine1', userData.currentAddressLine1],
      ['permanentAddressLine2', userData.currentAddressLine2],
      ['permanentState', userData.currentState],
      ['permanentDistrict', userData.currentDistrict],
      ['permanentCityVillage', userData.currentCityVillage],
      ['permanentPinCode', userData.currentPinCode],
    ] : [
      ['permanentAddressLine1', ''],
      ['permanentAddressLine2', ''],
      ['permanentState', ''],
      ['permanentDistrict', ''],
      ['permanentCityVillage', ''],
      ['permanentPinCode', ''],
    ];
    permanentPairs.forEach(([field, value]) => saveFieldNow(field, value));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      if (!currentUser) {
        logger.error('You must be logged in to upload files', null, { showToast: true });
        return;
      }

      // Initialize progress
      setUploadProgress(prev => ({
        ...prev,
        [fieldName]: { status: 'uploading', progress: 0 }
      }));

      // Compress image if it's an image file
      let fileToUpload: File | Blob = file;
      if (file.type.startsWith('image/')) {
        try {
          setCompressing(true);
          fileToUpload = await compressImage(file);
          setCompressing(false);
        } catch (error) {
          logger.error('Error compressing image:', error, { showToast: true });
          fileToUpload = file;
          setCompressing(false);
        }
      }

      // Upload to Cloudflare R2 via API
      setUploadProgress(prev => ({
        ...prev,
        [fieldName]: { status: 'uploading', progress: 50 }
      }));

      const response = await api.uploadFile(currentUser.uid, fieldName, fileToUpload as File);

      if (response.url) {
        const stateField = fieldName === 'vehicleRegistration' ? 'vehicleRegistrationUrl' : fieldName;
        setUserData(prev => ({
          ...prev,
          [stateField]: response.url
        }));
        setUploadProgress(prev => ({
          ...prev,
          [fieldName]: { status: 'completed', progress: 100 }
        }));
        logger.info(`${fieldName} uploaded successfully`, null, { showToast: true });
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      logger.error(`Error in ${fieldName} upload:`, error, { showToast: true });
      setUploadProgress(prev => ({
        ...prev,
        [fieldName]: { status: 'error', progress: 0 }
      }));
    }
  };

  const handleRemoveFile = async (fieldName: string) => {
    try {
      if (!currentUser) {
        logger.error('You must be logged in to remove files', null, { showToast: true });
        return;
      }

      // Only attempt to delete from storage if we have a valid URL
      if (hasValidFileUrl(fieldName)) {
        try {
          await api.deleteFile(currentUser.uid, fieldName);
        } catch (error) {
          logger.error(`Error deleting file from storage: ${error}`, null, { showToast: true });
          // Continue with updating the userData even if storage delete fails
        }
      }

      // Update the user data to remove the file URL
      const stateField = fieldName === 'vehicleRegistration' ? 'vehicleRegistrationUrl' : fieldName;
      setUserData(prev => ({
        ...prev,
        [stateField]: ''
      }));

      // Reset the upload progress
      setUploadProgress(prev => ({
        ...prev,
        [fieldName]: { status: 'idle', progress: 0 }
      }));

      logger.info(`${fieldName} removed successfully`, null, { showToast: true });
    } catch (error) {
      logger.error(`Error removing ${fieldName}:`, error, { showToast: true });
    }
  };

  const compressImage = async (file: File): Promise<File> => {
    const options = {
      maxSizeMB: 2,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      fileType: file.type as 'image/jpeg' | 'image/png',
      initialQuality: 0.9,
    };

    let compressedBlob = await imageCompression(file, options);
    
    let compressedFile = new File(
      [compressedBlob],
      file.name,
      {
        type: compressedBlob.type,
        lastModified: file.lastModified
      }
    );
    
    if (compressedFile.size > file.size) {
      compressedFile = file;
    }

    if (compressedFile.size > 2 * 1024 * 1024) {
      const progressiveOptions = {
        ...options,
        initialQuality: 0.8,
        maxSizeMB: 1.9
      };
      compressedBlob = await imageCompression(file, progressiveOptions);
      compressedFile = new File(
        [compressedBlob],
        file.name,
        {
          type: compressedBlob.type,
          lastModified: file.lastModified
        }
      );
    }

    return compressedFile;
  };

  const createHistoryEntry = (existingData: UserData | null, newData: UserData, user: { email: string }): UserHistory => {
    const changes: { [key: string]: { old: any; new: any } } = {};
    
    if (existingData) {
      Object.keys(newData).forEach(key => {
        // Skip certain fields from history tracking
        if (['history', 'createdAt', 'createdBy', 'updatedAt', 'lastUpdatedBy'].includes(key)) {
          return;
        }
        
        if (JSON.stringify(existingData[key]) !== JSON.stringify(newData[key])) {
          changes[key] = {
            old: existingData[key] || '',
            new: newData[key] || ''
          };
        }
      });
    } else {
      Object.keys(newData).forEach(key => {
        // Skip certain fields from history tracking
        if (['history', 'createdAt', 'createdBy', 'updatedAt', 'lastUpdatedBy'].includes(key)) {
          return;
        }
        
        changes[key] = {
          old: '',
          new: newData[key] || ''
        };
      });
    }

    return {
      timestamp: new Date().toISOString(),
      changes,
      updatedBy: user.email || 'system'
    };
  };

  const saveUserChanges = async (newData: UserData, existingData: UserData | null) => {
    // Create history entry using currentUser from context
    const historyEntry = createHistoryEntry(existingData, newData, { 
      email: currentUser?.email || 'system'
    });

    // Clean the data before updating
    const cleanData = (data: any): any => {
      const cleaned: any = {};
      for (const [key, value] of Object.entries(data)) {
        // Convert undefined or null to empty string
        if (value === undefined || value === null) {
          cleaned[key] = '';
        }
        // Convert Date objects to ISO string
        else if (value instanceof Date) {
          cleaned[key] = value.toISOString();
        }
        // Handle arrays (like history)
        else if (Array.isArray(value)) {
          cleaned[key] = value.map(item => 
            typeof item === 'object' ? cleanData(item) : (item || '')
          );
        }
        // Handle nested objects
        else if (typeof value === 'object' && value !== null) {
          cleaned[key] = cleanData(value);
        }
        // Handle all other values
        else {
          cleaned[key] = value;
        }
      }
      return cleaned;
    };

    // Update the user data with new timestamps and history
    const updatedData = cleanData({
      ...newData,
      updatedAt: new Date().toISOString(),
      lastUpdatedBy: currentUser?.email || 'system',
      history: [...(newData.history || []), historyEntry]
    });

    // If this is a new user, add creation info
    if (!existingData) {
      updatedData.createdAt = new Date().toISOString();
      updatedData.createdBy = currentUser?.email || 'system';
    }

    return { updatedData, historyEntry };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (!currentUser) {
        logger.error('You must be logged in to update details', null, { showToast: true });
        return;
      }

      // Flush any debounced auto-saves so every typed value reaches the database
      const pendingFields = Object.keys(saveTimersRef.current);
      pendingFields.forEach(f => clearTimeout(saveTimersRef.current[f]));
      saveTimersRef.current = {};
      for (const field of pendingFields) {
        const value = latestValuesRef.current[field];
        if (value !== undefined) {
          await api.updateUser(currentUser.uid, { [field]: value ?? '' }).catch(() => {});
        }
      }

      if (WITNESS_FIELDS.some(f => witnessDuplicateError(f))) {
        toast.error('Witness details must be unique and different from the member. Please correct them.');
        setIsLoading(false);
        return;
      }

      // Prepare data for update
      const updateData = {
        fullName: userData.fullName,
        phone: userData.phone,
        dateOfBirth: userData.dateOfBirth,
        bloodGroup: userData.bloodGroup,
        gender: userData.gender,
        fatherOrHusbandName: userData.fatherOrHusbandName,
        education: userData.education,
        languagesKnown: userData.languagesKnown,
        identificationMark: userData.identificationMark,
        profession: userData.profession,
        vehicleMake: userData.vehicleMake,
        vehicleModel: userData.vehicleModel,
        vehicleRegistration: userData.vehicleRegistration,
        currentAddressLine1: userData.currentAddressLine1,
        currentAddressLine2: userData.currentAddressLine2,
        currentState: userData.currentState,
        currentDistrict: userData.currentDistrict,
        currentCityVillage: userData.currentCityVillage,
        currentPinCode: userData.currentPinCode,
        permanentAddressLine1: userData.permanentAddressLine1,
        permanentAddressLine2: userData.permanentAddressLine2,
        permanentState: userData.permanentState,
        permanentDistrict: userData.permanentDistrict,
        permanentCityVillage: userData.permanentCityVillage,
        permanentPinCode: userData.permanentPinCode,
        witness1Name: userData.witness1Name,
        witness1Phone: userData.witness1Phone,
        witness1Aadhaar: userData.witness1Aadhaar,
        witness2Name: userData.witness2Name,
        witness2Phone: userData.witness2Phone,
        witness2Aadhaar: userData.witness2Aadhaar,
        photo: userData.photo,
        aadhaarFront: userData.aadhaarFront,
        aadhaarBack: userData.aadhaarBack,
        profileUpdated: true
      };

      await api.updateUser(currentUser.uid, updateData);

      // Refresh context so profileUpdated=true is visible to Dashboard redirects
      try {
        await refreshUserDetails();
      } catch (refreshError) {
        logger.error('Failed to refresh user details after save', refreshError, { showToast: false });
      }

      logger.info('User details updated successfully', null, { showToast: true });
      toast.success('Profile completed successfully');
      navigate('/dashboard', { replace: true });
    } catch (error) {
      logger.error('Error updating user details:', error, { showToast: true });
    } finally {
      setIsLoading(false);
    }
  };



  const inputClassName = `
    w-full px-4 py-3 
    bg-slate-800/40 
    border border-slate-700/50 
    rounded-lg 
    focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 
    transition-all duration-200
    text-slate-200
    [-webkit-text-fill-color:rgb(226,232,240)]
    [&::placeholder]:[-webkit-text-fill-color:rgb(100,116,139)]
    [&::-webkit-input-placeholder]:[-webkit-text-fill-color:rgb(100,116,139)]
    [&:-webkit-autofill]:[background-color:transparent]
    [&:-webkit-autofill]:[box-shadow:0_0_0_1000px_transparent_inset]
    [&:-webkit-autofill]:[-webkit-text-fill-color:rgb(226,232,240)]
    [&:-webkit-autofill]:[transition:background-color_5000s_ease-in-out_0s]
  `;

  const labelClassName = `
    block text-sm font-medium 
    text-slate-400 
    mb-1.5
  `;

  const requiredLabelClassName = `
    block text-sm font-medium 
    text-slate-400 
    mb-1.5
    after:content-['*'] 
    after:ml-1 
    after:text-red-500
  `;

  const sectionClassName = `
    bg-slate-900/50 
    backdrop-blur-sm 
    rounded-xl 
    border border-slate-800/50 
    p-6 md:p-8 
    space-y-6
    shadow-lg 
    shadow-slate-900/20
    transition-all duration-300 
    hover:shadow-slate-900/30
  `;

  const sectionHeaderClassName = `
    text-xl font-semibold 
    text-emerald-400
    mb-6 
    flex items-center gap-2
    border-b border-slate-800/50 
    pb-4
  `;

  const gridClassName = `
    grid grid-cols-1 
    md:grid-cols-2 
    lg:grid-cols-4 
    gap-6
  `;

  const buttonClassName = `
    px-6 py-2.5 
    rounded-lg 
    font-medium 
    transition-all duration-200 
    focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900
  `;

  const hasValidFileUrl = (fieldName: string) => {
    const stateField = fieldName === 'vehicleRegistration' ? 'vehicleRegistrationUrl' : fieldName;
    return userData[stateField] && typeof userData[stateField] === 'string' && userData[stateField].length > 0;
  };

  // Per-field save status: spinner while saving, green check once stored, red on failure
  const FieldStatus: React.FC<{ field: string }> = ({ field }) => {
    const status = fieldStatus[field];
    if (!status || status === 'idle') return null;
    if (status === 'saving') {
      return (
        <span className="flex items-center gap-1 text-[11px] text-slate-400 whitespace-nowrap">
          <span className="w-3 h-3 border-t-2 border-r-2 border-slate-400 rounded-full animate-spin"></span>
          Saving...
        </span>
      );
    }
    if (status === 'saved') {
      return (
        <span className="flex items-center gap-1 text-[11px] text-emerald-400 whitespace-nowrap">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
          </svg>
          Updated
        </span>
      );
    }
    return <span className="text-[11px] text-red-400 whitespace-nowrap">⚠ Not saved</span>;
  };

  const FieldLabel: React.FC<{ label: string; field: string; required?: boolean }> = ({ label, field, required }) => (
    <div className="flex items-center justify-between mb-1.5 gap-2">
      <label className={`${required ? "block text-sm font-medium text-slate-400 after:content-['*'] after:ml-1 after:text-red-500" : "block text-sm font-medium text-slate-400"}`}>{label}</label>
      <FieldStatus field={field} />
    </div>
  );

  useEffect(() => {
    if (authLoading) return;

    if (!currentUser) {
      setIsFetching(false);
      navigate('/login');
      return;
    }

    // Use authUserDetails from context
    if (authUserDetails) {
      setUserData(prevData => ({
        ...prevData,
        fullName: authUserDetails.fullName || '',
        email: authUserDetails.email || currentUser.email || '',
        phone: formatPhoneForDisplay(authUserDetails.phone || ''),
        aadhaarNumber: authUserDetails.aadhaarNumber || '',
        memberType: authUserDetails.memberType || 'National',
        memberStatus: authUserDetails.memberStatus || 'Active',
        designation: authUserDetails.designation || '',
        enrollmentNumber: authUserDetails.enrollmentNumber || '',
        validFrom: authUserDetails.validFrom || new Date().toISOString().split('T')[0],
        currentAddressLine1: authUserDetails.currentAddressLine1 || '',
        currentAddressLine2: authUserDetails.currentAddressLine2 || '',
        currentState: authUserDetails.currentState || '',
        currentDistrict: authUserDetails.currentDistrict || '',
        currentCityVillage: authUserDetails.currentCityVillage || '',
        currentPinCode: authUserDetails.currentPinCode || '',
        permanentAddressLine1: authUserDetails.permanentAddressLine1 || '',
        permanentAddressLine2: authUserDetails.permanentAddressLine2 || '',
        permanentState: authUserDetails.permanentState || '',
        permanentDistrict: authUserDetails.permanentDistrict || '',
        permanentCityVillage: authUserDetails.permanentCityVillage || '',
        permanentPinCode: authUserDetails.permanentPinCode || '',
        witness1Name: authUserDetails.witness1Name || '',
        witness1Phone: formatPhoneForDisplay(authUserDetails.witness1Phone || ''),
        witness1Aadhaar: authUserDetails.witness1Aadhaar || '',
        witness2Name: authUserDetails.witness2Name || '',
        witness2Phone: formatPhoneForDisplay(authUserDetails.witness2Phone || ''),
        witness2Aadhaar: authUserDetails.witness2Aadhaar || '',
        photo: authUserDetails.photo || '',
        aadhaarFront: authUserDetails.aadhaarFront || '',
        aadhaarBack: authUserDetails.aadhaarBack || '',
        vehicleRegistration: authUserDetails.vehicleRegistration || '',
        vehicleRegistrationUrl: authUserDetails.vehicleRegistrationUrl || '',
        promissoryNote: authUserDetails.promissoryNote || '',
        gender: (authUserDetails.gender || '').toLowerCase(),
        dateOfBirth: authUserDetails.dateOfBirth || '',
        bloodGroup: authUserDetails.bloodGroup || '',
        fatherOrHusbandName: authUserDetails.fatherOrHusbandName || '',
        education: authUserDetails.education || '',
        languagesKnown: authUserDetails.languagesKnown || '',
        identificationMark: authUserDetails.identificationMark || '',
        profession: authUserDetails.profession || '',
        vehicleMake: authUserDetails.vehicleMake || '',
        vehicleModel: authUserDetails.vehicleModel || '',
        profileUpdated: authUserDetails.profileUpdated || false
      }));
      setIsFetching(false);
    }
  }, [currentUser, authUserDetails, authLoading, navigate]);

  useEffect(() => {
    if (authLoading) return;

    if (!currentUser) {
      navigate('/login');
      return;
    }

    // If profile is already updated and user opens the form again, send them back
    // to the dashboard (form should not reappear after completion).
    if (authUserDetails?.profileUpdated) {
      navigate('/dashboard', { replace: true });
    }
  }, [currentUser, authUserDetails, authLoading, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 text-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-emerald-400">Update Details</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Personal Details */}
          <div className={sectionClassName}>
            <h2 className={sectionHeaderClassName}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Personal Details
            </h2>
            <div className={gridClassName}>
              <div>
                <FieldLabel label="Full Name" field="fullName" required />
                <input
                  type="text"
                  name="fullName"
                  value={getInputValue('fullName')}
                  onChange={handleInputChange}
                  className={inputClassName}
                  placeholder="Enter full name"
                  required
                />
              </div>
              <div>
                <label className={requiredLabelClassName}>Email</label>
                <input
                  type="email"
                  name="email"
                  value={getInputValue('email')}
                  className={inputClassName}
                  placeholder="Email will be copied from Authentication Details"
                  disabled
                />
              </div>
              <div>
                <FieldLabel label="Phone Number" field="phone" required />
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                    <span className="text-slate-500 select-none">+91</span>
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    value={getPhoneDigits(userData.phone)}
                    onChange={handlePhoneDigits('phone')}
                    className={`${inputClassName} pl-20`}
                    placeholder="Mobile Number"
                    inputMode="numeric"
                    maxLength={16}
                    required
                  />
                </div>
              </div>
              <div>
                <FieldLabel label="Gender" field="gender" required />
                <SearchableSelect
                  name="gender"
                  value={getInputValue('gender')}
                  onChange={handleInputChange}
                  required
                  placeholder="Select Gender"
                  customPlaceholder="Enter other gender"
                  options={[
                    { value: 'male', label: 'Male' },
                    { value: 'female', label: 'Female' },
                    { value: 'other', label: 'Other' },
                  ]}
                />
              </div>
              <div>
                <FieldLabel label="Date of Birth" field="dateOfBirth" required />
                <input
                  type="date"
                  name="dateOfBirth"
                  value={getInputValue('dateOfBirth')}
                  onChange={handleInputChange}
                  className={inputClassName}
                  required
                />
              </div>
              <div>
                <FieldLabel label="Aadhaar Number" field="aadhaarNumber" required />
                <input
                  type="text"
                  name="aadhaarNumber"
                  value={getInputValue('aadhaarNumber')}
                  onChange={handleInputChange}
                  className={inputClassName}
                  placeholder="Enter Aadhaar number"
                  required
                />
              </div>
              <div>
                <FieldLabel label="Blood Group" field="bloodGroup" required />
                <SearchableSelect
                  name="bloodGroup"
                  value={getInputValue('bloodGroup')}
                  onChange={handleInputChange}
                  required
                  placeholder="Select Blood Group"
                  customPlaceholder="Enter other blood group"
                  options={['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map((group) => ({ value: group, label: group }))}
                />
              </div>
              <div>
                <FieldLabel label="Father/Husband Name" field="fatherOrHusbandName" required />
                <input
                  type="text"
                  name="fatherOrHusbandName"
                  value={getInputValue('fatherOrHusbandName')}
                  onChange={handleInputChange}
                  className={inputClassName}
                  placeholder="Enter father's/husband's name"
                  required
                />
              </div>
              <div>
                <FieldLabel label="Education" field="education" required />
                <SearchableSelect
                  name="education"
                  value={getInputValue('education')}
                  onChange={handleInputChange}
                  required
                  placeholder="Select Education"
                  customPlaceholder="Enter other education"
                  options={EDUCATION_OPTIONS.map((option) => ({ value: option, label: option }))}
                />
              </div>
              <div>
                <FieldLabel label="Languages Known" field="languagesKnown" required />
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      if (!languagesOpen) {
                        setLanguagesSearch('');
                        setCustomLanguageMode(false);
                        setCustomLanguage('');
                      }
                      setLanguagesOpen(!languagesOpen);
                    }}
                    className={`${inputClassName} text-left flex items-center justify-between`}
                  >
                    <span className={getInputValue('languagesKnown') ? '' : '[&::placeholder]:[-webkit-text-fill-color:rgb(100,116,139)] [-webkit-text-fill-color:rgb(100,116,139)]'}>
                      {getInputValue('languagesKnown') || 'Select languages'}
                    </span>
                    <svg className={`w-4 h-4 flex-shrink-0 ml-2 transition-transform duration-200 ${languagesOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {languagesOpen && (
                    <div className="absolute z-20 mt-1 w-full bg-slate-800 border border-slate-700/50 rounded-lg shadow-xl overflow-hidden">
                      <div className="border-b border-slate-700/50 p-2">
                        <input
                          type="text"
                          value={languagesSearch}
                          onChange={(e) => setLanguagesSearch(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                          placeholder="Search languages..."
                          className="w-full rounded-md border border-slate-600/60 bg-slate-800/60 px-3 py-2 text-sm text-slate-200 placeholder:text-slate-500 focus:border-emerald-500/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                        />
                      </div>
                      <div className="max-h-64 overflow-y-auto p-2 grid grid-cols-2 gap-1">
                        {allLanguageOptions
                          .filter(([value]) => value.toLowerCase().includes(languagesSearch.trim().toLowerCase()))
                          .map(([value, label]) => (
                            <label key={value} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-700/50 cursor-pointer text-sm text-slate-200">
                              <input
                                type="checkbox"
                                checked={getInputValue('languagesKnown').split(',').map(s => s.trim()).includes(value)}
                                onChange={() => handleLanguageToggle(value)}
                                className="w-4 h-4 rounded border-gray-500/30 bg-gray-800/50 text-blue-500 focus:ring-blue-500/30 focus:ring-offset-0"
                              />
                              {label}
                            </label>
                          ))}
                        {allLanguageOptions.filter(([value]) => value.toLowerCase().includes(languagesSearch.trim().toLowerCase())).length === 0 && (
                          <div className="col-span-2 px-2 py-3 text-sm text-slate-500">No languages found</div>
                        )}
                      </div>
                      {customLanguageMode ? (
                        <div className="border-t border-slate-700/50 space-y-2 p-2">
                          <input
                            type="text"
                            value={customLanguage}
                            onChange={(e) => setCustomLanguage(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                e.stopPropagation();
                                addCustomLanguage();
                              }
                            }}
                            placeholder="Enter other language"
                            className="w-full rounded-md border border-slate-600/60 bg-slate-800/60 px-3 py-2 text-sm text-slate-200 placeholder:text-slate-500 focus:border-emerald-500/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                          />
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => { setCustomLanguageMode(false); setCustomLanguage(''); }}
                              className="rounded-md border border-slate-600/60 px-3 py-1.5 text-sm text-slate-300 transition-colors hover:bg-slate-800"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={addCustomLanguage}
                              disabled={!customLanguage.trim()}
                              className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              Add
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setCustomLanguageMode(true)}
                          className="flex w-full items-center gap-2 border-t border-slate-700/50 bg-slate-800 px-4 py-2.5 text-left text-sm font-medium text-emerald-400 transition-colors hover:bg-emerald-500/10"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                          </svg>
                          Other — add manually
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <FieldLabel label="Identification Mark" field="identificationMark" required />
                <input
                  type="text"
                  name="identificationMark"
                  value={getInputValue('identificationMark')}
                  onChange={handleInputChange}
                  className={inputClassName}
                  placeholder="Enter identification mark"
                  required
                />
              </div>
              <div>
                <FieldLabel label="Profession" field="profession" required />
                <input
                  type="text"
                  name="profession"
                  value={getInputValue('profession')}
                  onChange={handleInputChange}
                  className={inputClassName}
                  placeholder="Enter profession"
                  required
                />
              </div>
            </div>
          </div>

          {/* Current Address */}
          <div className={sectionClassName}>
            <h2 className={sectionHeaderClassName}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Current Address
            </h2>
            <div className={gridClassName}>
              <div>
                <FieldLabel label="Address Line 1" field="currentAddressLine1" required />
                <input
                  type="text"
                  name="currentAddressLine1"
                  value={getInputValue('currentAddressLine1')}
                  onChange={handleInputChange}
                  className={inputClassName}
                  placeholder="Enter address line 1"
                  required
                />
              </div>
              <div>
                <FieldLabel label="Address Line 2" field="currentAddressLine2" required />
                <input
                  type="text"
                  name="currentAddressLine2"
                  value={getInputValue('currentAddressLine2')}
                  onChange={handleInputChange}
                  className={inputClassName}
                  placeholder="Enter address line 2"
                />
              </div>
              <div>
                <FieldLabel label="State" field="currentState" required />
                <input
                  type="text"
                  name="currentState"
                  value={getInputValue('currentState')}
                  onChange={handleInputChange}
                  className={inputClassName}
                  placeholder="Enter state"
                  required
                />
              </div>
              <div>
                <FieldLabel label="District" field="currentDistrict" required />
                <input
                  type="text"
                  name="currentDistrict"
                  value={getInputValue('currentDistrict')}
                  onChange={handleInputChange}
                  className={inputClassName}
                  placeholder="Enter district"
                  required
                />
              </div>
              <div>
                <FieldLabel label="City/Village" field="currentCityVillage" required />
                <input
                  type="text"
                  name="currentCityVillage"
                  value={getInputValue('currentCityVillage')}
                  onChange={handleInputChange}
                  className={inputClassName}
                  placeholder="Enter city/village"
                  required
                />
              </div>
              <div>
                <FieldLabel label="Pin Code" field="currentPinCode" required />
                <input
                  type="text"
                  name="currentPinCode"
                  value={getInputValue('currentPinCode')}
                  onChange={handleInputChange}
                  className={inputClassName}
                  placeholder="Enter PIN code"
                  required
                />
              </div>
            </div>
          </div>

          {/* Permanent Address */}
          <div className={sectionClassName}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={sectionHeaderClassName}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5m3-3h1" />
                </svg>
                Permanent Address
              </h2>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="sameAsCurrent"
                  name="sameAsCurrent"
                  checked={userData.sameAsCurrent}
                  onChange={handleSameAsCurrentChange}
                  className="w-4 h-4 rounded border-gray-500/30 bg-gray-800/50 
                   text-blue-500 focus:ring-blue-500/30 focus:ring-offset-0
                   transition-all duration-300"
                />
                <label htmlFor="sameAsCurrent" className="text-sm text-gray-300 hover:text-blue-400 transition-colors duration-300">
                  Same as Current Address
                </label>
              </div>
            </div>
            <div className={gridClassName}>
              <div>
                <FieldLabel label="Address Line 1" field="permanentAddressLine1" required />
                <input
                  type="text"
                  name="permanentAddressLine1"
                  value={getInputValue('permanentAddressLine1')}
                  onChange={handleInputChange}
                  className={inputClassName}
                  placeholder="Enter address line 1"
                  required
                  disabled={userData.sameAsCurrent}
                />
              </div>
              <div>
                <FieldLabel label="Address Line 2" field="permanentAddressLine2" />
                <input
                  type="text"
                  name="permanentAddressLine2"
                  value={getInputValue('permanentAddressLine2')}
                  onChange={handleInputChange}
                  className={inputClassName}
                  placeholder="Enter address line 2"
                  disabled={userData.sameAsCurrent}
                />
              </div>
              <div>
                <FieldLabel label="State" field="permanentState" required />
                <input
                  type="text"
                  name="permanentState"
                  value={getInputValue('permanentState')}
                  onChange={handleInputChange}
                  className={inputClassName}
                  placeholder="Enter state"
                  required
                  disabled={userData.sameAsCurrent}
                />
              </div>
              <div>
                <FieldLabel label="District" field="permanentDistrict" required />
                <input
                  type="text"
                  name="permanentDistrict"
                  value={getInputValue('permanentDistrict')}
                  onChange={handleInputChange}
                  className={inputClassName}
                  placeholder="Enter district"
                  required
                  disabled={userData.sameAsCurrent}
                />
              </div>
              <div>
                <FieldLabel label="City/Village" field="permanentCityVillage" required />
                <input
                  type="text"
                  name="permanentCityVillage"
                  value={getInputValue('permanentCityVillage')}
                  onChange={handleInputChange}
                  className={inputClassName}
                  placeholder="Enter city/village"
                  required
                  disabled={userData.sameAsCurrent}
                />
              </div>
              <div>
                <FieldLabel label="Pin Code" field="permanentPinCode" required />
                <input
                  type="text"
                  name="permanentPinCode"
                  value={getInputValue('permanentPinCode')}
                  onChange={handleInputChange}
                  className={inputClassName}
                  placeholder="Enter PIN code"
                  required
                  disabled={userData.sameAsCurrent}
                />
              </div>
            </div>
          </div>

          {/* Vehicle Details */}
          {(userData.vehicleMake || userData.vehicleModel || userData.vehicleRegistration) && (
          <div className={sectionClassName}>
            <h2 className={sectionHeaderClassName}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 16v3a2 2 0 01-2 2H7a2 2 0 01-2-2v-3m14 0V7a2 2 0 00-2-2H7a2 2 0 00-2 2v9m14 0h-3m-9 0H3m2 0h3m3-3h1" />
              </svg>
              Vehicle Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <FieldLabel label="Make" field="vehicleMake" />
                <input
                  type="text"
                  name="vehicleMake"
                  value={getInputValue('vehicleMake')}
                  onChange={handleInputChange}
                  className={inputClassName}
                  placeholder="Enter vehicle make"
                />
              </div>
              <div>
                <FieldLabel label="Model" field="vehicleModel" />
                <input
                  type="text"
                  name="vehicleModel"
                  value={getInputValue('vehicleModel')}
                  onChange={handleInputChange}
                  className={inputClassName}
                  placeholder="Enter vehicle model"
                />
              </div>
              <div>
                <FieldLabel label="Registration Number" field="vehicleRegistration" />
                <input
                  type="text"
                  name="vehicleRegistration"
                  value={getInputValue('vehicleRegistration')}
                  onChange={handleInputChange}
                  className={inputClassName}
                  placeholder="Enter registration number"
                />
              </div>
            </div>
          </div>
          )}

          {/* Witness Details */}
          <div className={sectionClassName}>
            <h2 className={sectionHeaderClassName}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Witness Details
            </h2>
            
            {/* Witness 1 */}
            <div className="mb-6">
              <h3 className="text-lg font-medium mb-4 text-white border-b border-white/10 pb-2">Witness 1</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <FieldLabel label="Full Name" field="witness1Name" required />
                  <input
                    type="text"
                    name="witness1Name"
                    value={getInputValue('witness1Name')}
                    onChange={handleInputChange}
                    className={inputClassName}
                    placeholder="Enter witness full name"
                    required
                  />
                  {witnessDuplicateError('witness1Name') && (
                    <p className="text-red-400 text-xs mt-1">⚠️ {witnessDuplicateError('witness1Name')} — must be different</p>
                  )}
                </div>
                <div>
                  <FieldLabel label="Phone Number" field="witness1Phone" required />
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                      <span className="text-slate-500 select-none">+91</span>
                    </div>
                    <input
                      type="tel"
                      name="witness1Phone"
                      value={getPhoneDigits(userData.witness1Phone)}
                      onChange={handlePhoneDigits('witness1Phone')}
                      className={`${inputClassName} pl-20`}
                      placeholder="Mobile Number"
                      inputMode="numeric"
                      maxLength={16}
                      required
                    />
                  </div>
                  {witnessDuplicateError('witness1Phone') && (
                    <p className="text-red-400 text-xs mt-1">⚠️ {witnessDuplicateError('witness1Phone')} — must be different</p>
                  )}
                </div>
                <div>
                  <FieldLabel label="Aadhaar Number" field="witness1Aadhaar" required />
                  <input
                    type="text"
                    name="witness1Aadhaar"
                    value={getInputValue('witness1Aadhaar')}
                    onChange={handleInputChange}
                    className={inputClassName}
                    placeholder="Enter Aadhaar number"
                    required
                  />
                  {witnessDuplicateError('witness1Aadhaar') && (
                    <p className="text-red-400 text-xs mt-1">⚠️ {witnessDuplicateError('witness1Aadhaar')} — must be different</p>
                  )}
                </div>
              </div>
            </div>

            {/* Witness 2 */}
            <div>
              <h3 className="text-lg font-medium mb-4 text-white border-b border-white/10 pb-2">Witness 2</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <FieldLabel label="Full Name" field="witness2Name" required />
                  <input
                    type="text"
                    name="witness2Name"
                    value={getInputValue('witness2Name')}
                    onChange={handleInputChange}
                    className={inputClassName}
                    placeholder="Enter witness full name"
                    required
                  />
                  {witnessDuplicateError('witness2Name') && (
                    <p className="text-red-400 text-xs mt-1">⚠️ {witnessDuplicateError('witness2Name')} — must be different</p>
                  )}
                </div>
                <div>
                  <FieldLabel label="Phone Number" field="witness2Phone" required />
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                      <span className="text-slate-500 select-none">+91</span>
                    </div>
                    <input
                      type="tel"
                      name="witness2Phone"
                      value={getPhoneDigits(userData.witness2Phone)}
                      onChange={handlePhoneDigits('witness2Phone')}
                      className={`${inputClassName} pl-20`}
                      placeholder="Mobile Number"
                      inputMode="numeric"
                      maxLength={16}
                      required
                    />
                  </div>
                  {witnessDuplicateError('witness2Phone') && (
                    <p className="text-red-400 text-xs mt-1">⚠️ {witnessDuplicateError('witness2Phone')} — must be different</p>
                  )}
                </div>
                <div>
                  <FieldLabel label="Aadhaar Number" field="witness2Aadhaar" required />
                  <input
                    type="text"
                    name="witness2Aadhaar"
                    value={getInputValue('witness2Aadhaar')}
                    onChange={handleInputChange}
                    className={inputClassName}
                    placeholder="Enter Aadhaar number"
                    required
                  />
                  {witnessDuplicateError('witness2Aadhaar') && (
                    <p className="text-red-400 text-xs mt-1">⚠️ {witnessDuplicateError('witness2Aadhaar')} — must be different</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Document Uploads */}
          <div className={sectionClassName}>
            <h2 className={sectionHeaderClassName}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Document Uploads
            </h2>
            <div className={gridClassName}>
              {/* Photo Upload */}
              <div>
                <label className={requiredLabelClassName}>Photo</label>
                <div className="flex items-center space-x-4">
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'photo')}
                    className={`
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-full file:border-0
                      file:text-sm file:font-semibold
                      file:bg-emerald-50 file:text-emerald-700
                      hover:file:bg-emerald-100
                      text-slate-400
                      w-full
                      cursor-pointer
                    `}
                    required
                  />
                  {uploadProgress.photo?.status === 'uploading' && (
                    <div className="h-1 w-full bg-gray-700 rounded">
                      <div
                        className="h-1 bg-emerald-500 rounded transition-all duration-300"
                        style={{ width: `${uploadProgress.photo.progress}%` }}
                      />
                    </div>
                  )}
                  {hasValidFileUrl('photo') && (
                    <button
                      type="button"
                      onClick={() => handleRemoveFile('photo')}
                      className="text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  )}
                </div>
                {hasValidFileUrl('photo') && (
                  <a
                    href={userData.photo}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-500 hover:text-emerald-400 text-sm mt-1 inline-block"
                  >
                    View uploaded photo
                  </a>
                )}
              </div>

              {/* Aadhaar Front Upload */}
              <div>
                <label className={requiredLabelClassName}>Aadhaar Front</label>
                <div className="flex items-center space-x-4">
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'aadhaarFront')}
                    className={`
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-full file:border-0
                      file:text-sm file:font-semibold
                      file:bg-emerald-50 file:text-emerald-700
                      hover:file:bg-emerald-100
                      text-slate-400
                      w-full
                      cursor-pointer
                    `}
                    required
                  />
                  {uploadProgress.aadhaarFront?.status === 'uploading' && (
                    <div className="h-1 w-full bg-gray-700 rounded">
                      <div
                        className="h-1 bg-emerald-500 rounded transition-all duration-300"
                        style={{ width: `${uploadProgress.aadhaarFront.progress}%` }}
                      />
                    </div>
                  )}
                  {hasValidFileUrl('aadhaarFront') && (
                    <button
                      type="button"
                      onClick={() => handleRemoveFile('aadhaarFront')}
                      className="text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  )}
                </div>
                {hasValidFileUrl('aadhaarFront') && (
                  <a
                    href={userData.aadhaarFront}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-500 hover:text-emerald-400 text-sm mt-1 inline-block"
                  >
                    View uploaded Aadhaar front
                  </a>
                )}
              </div>

              {/* Aadhaar Back Upload */}
              <div>
                <label className={requiredLabelClassName}>Aadhaar Back</label>
                <div className="flex items-center space-x-4">
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'aadhaarBack')}
                    className={`
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-full file:border-0
                      file:text-sm file:font-semibold
                      file:bg-emerald-50 file:text-emerald-700
                      hover:file:bg-emerald-100
                      text-slate-400
                      w-full
                      cursor-pointer
                    `}
                    required
                  />
                  {uploadProgress.aadhaarBack?.status === 'uploading' && (
                    <div className="h-1 w-full bg-gray-700 rounded">
                      <div
                        className="h-1 bg-emerald-500 rounded transition-all duration-300"
                        style={{ width: `${uploadProgress.aadhaarBack.progress}%` }}
                      />
                    </div>
                  )}
                  {hasValidFileUrl('aadhaarBack') && (
                    <button
                      type="button"
                      onClick={() => handleRemoveFile('aadhaarBack')}
                      className="text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  )}
                </div>
                {hasValidFileUrl('aadhaarBack') && (
                  <a
                    href={userData.aadhaarBack}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-500 hover:text-emerald-400 text-sm mt-1 inline-block"
                  >
                    View uploaded Aadhaar back
                  </a>
                )}
              </div>

              {/* Vehicle Registration Upload */}
              <div>
                <label className={labelClassName}>Vehicle Registration</label>
                <div className="flex items-center space-x-4">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'vehicleRegistration')}
                    className={`
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-full file:border-0
                      file:text-sm file:font-semibold
                      file:bg-emerald-50 file:text-emerald-700
                      hover:file:bg-emerald-100
                      text-slate-400
                      w-full
                      cursor-pointer
                    `}
                  />
                  {uploadProgress.vehicleRegistration?.status === 'uploading' && (
                    <div className="h-1 w-full bg-gray-700 rounded">
                      <div
                        className="h-1 bg-emerald-500 rounded transition-all duration-300"
                        style={{ width: `${uploadProgress.vehicleRegistration.progress}%` }}
                      />
                    </div>
                  )}
                  {hasValidFileUrl('vehicleRegistration') && (
                    <button
                      type="button"
                      onClick={() => handleRemoveFile('vehicleRegistration')}
                      className="text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  )}
                </div>
                {hasValidFileUrl('vehicleRegistration') && (
                  <a
                    href={userData.vehicleRegistrationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-500 hover:text-emerald-400 text-sm mt-1 inline-block"
                  >
                    View uploaded vehicle registration
                  </a>
                )}
              </div>

              {/* Promissory Note Upload */}
              <div>
                <label className={requiredLabelClassName}>Promissory Note</label>
                <div className="flex items-center space-x-4">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'promissoryNote')}
                    className={`
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-full file:border-0
                      file:text-sm file:font-semibold
                      file:bg-emerald-50 file:text-emerald-700
                      hover:file:bg-emerald-100
                      text-slate-400
                      w-full
                      cursor-pointer
                    `}
                    required
                  />
                  {uploadProgress.promissoryNote?.status === 'uploading' && (
                    <div className="h-1 w-full bg-gray-700 rounded">
                      <div
                        className="h-1 bg-emerald-500 rounded transition-all duration-300"
                        style={{ width: `${uploadProgress.promissoryNote.progress}%` }}
                      />
                    </div>
                  )}
                  {hasValidFileUrl('promissoryNote') && (
                    <button
                      type="button"
                      onClick={() => handleRemoveFile('promissoryNote')}
                      className="text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  )}
                </div>
                {hasValidFileUrl('promissoryNote') && (
                  <a
                    href={userData.promissoryNote}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-500 hover:text-emerald-400 text-sm mt-1 inline-block"
                  >
                    View uploaded promissory note
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="sticky bottom-0 bg-slate-900/90 backdrop-blur-sm border-t border-slate-800 py-4 z-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-end gap-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`${buttonClassName} ${
                    isLoading
                      ? 'bg-emerald-600/50 cursor-not-allowed'
                      : 'bg-emerald-600 hover:bg-emerald-700'
                  } text-white focus:ring-emerald-500`}
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-t-2 border-r-2 border-white rounded-full animate-spin"></div>
                      <span>Updating...</span>
                    </div>
                  ) : (
                    'Update Details'
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserDetails;
